import React from "react";
import ReactApexChart from "react-apexcharts";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import {
  KpiCard,
  SectionSpinner,
  SectionEmpty,
  exportXLSX,
} from "../shared/utils";
import { useSalesReport } from "./useSalesReport";
import SalesReportShell from "./SalesReportShell";
import { FiDownload } from "react-icons/fi";
import Button from "../../../../components/BaseUI/Button";

const fmtTL = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
      }).format(v);

const SalesTrendReport = () => {
  useTitle("Rovion MES - Sipariş Trendi");
  const {
    years,
    year,
    handleYearChange,
    salesData,
    loading,
    derived,
    toasts,
    dismissToast,
  } = useSalesReport();

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!salesData || !derived)
      return <SectionEmpty msg="Sipariş verisi bulunamadı." />;

    const { labels, revenueSeries, countSeries, deviceSeries } = derived;
    const cm = salesData.currentMonth || {};
    const pm = salesData.previousMonth || {};

    const revenueChart = {
      series: [{ name: "Ciro (₺)", data: revenueSeries }],
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: labels,
          labels: { rotate: -30, style: { fontSize: "11px" } },
        },
        yaxis: { labels: { formatter: (v) => `₺${(v / 1000).toFixed(0)}K` } },
        colors: ["#4472C4"],
        tooltip: { y: { formatter: fmtTL } },
        grid: { borderColor: "#e9ecef" },
      },
    };

    const salesCountChart = {
      series: [{ name: "Sipariş Adedi", data: countSeries }],
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: labels,
          labels: { rotate: -30, style: { fontSize: "11px" } },
        },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        colors: ["#2ecc71"],
        grid: { borderColor: "#e9ecef" },
      },
    };

    const deviceCountChart = {
      series: [{ name: "Ürün Adedi", data: deviceSeries }],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          fontFamily: "inherit",
        },
        stroke: { width: 2, curve: "smooth" },
        dataLabels: { enabled: false },
        xaxis: {
          categories: labels,
          labels: { rotate: -30, style: { fontSize: "11px" } },
        },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        colors: ["#e67e22"],
        grid: { borderColor: "#e9ecef" },
      },
    };

    const exportRows = (derived.labels || []).map((lbl, i) => ({
      Ay: lbl,
      "Sipariş Adedi": countSeries[i],
      "Ürün Adedi": deviceSeries[i],
      "Ciro (₺)": revenueSeries[i],
    }));

    return (
      <>
        <CardContainer className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2 p-2">
            <h5 className="mb-0">Aylık Ciro ({year})</h5>
            <Button
              size="sm"
              appearance="subtle"
              onClick={() => exportXLSX(exportRows, `satis-trendi-${year}`)}
            >
              <FiDownload size={14} className="me-1" /> Excel
            </Button>
          </div>
          <ReactApexChart
            options={revenueChart.options}
            series={revenueChart.series}
            type="bar"
            height={300}
          />
        </CardContainer>

        <CardContainer className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2 p-2">
            <h5 className="mb-0">Aylık Sipariş Adedi ({year})</h5>
          </div>
          <ReactApexChart
            options={salesCountChart.options}
            series={salesCountChart.series}
            type="bar"
            height={280}
          />
        </CardContainer>

        <CardContainer>
          <div className="d-flex justify-content-between align-items-center mb-2 p-2">
            <h5 className="mb-0">Aylık Ürün Adedi ({year})</h5>
          </div>
          <ReactApexChart
            options={deviceCountChart.options}
            series={deviceCountChart.series}
            type="line"
            height={280}
          />
        </CardContainer>
      </>
    );
  };

  return (
    <SalesReportShell
      title="Sipariş Trendi"
      year={year}
      years={years}
      onYearChange={handleYearChange}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </SalesReportShell>
  );
};

export default withAuth(SalesTrendReport);
